import React, { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Input,
  MenuProps,
  Select,
  Tooltip,
} from 'antd';
import {
  useAgentSelector,
  useAgentDispatch,
} from '../../../../hooks/useAgentStore';
import {
  ChangeRequestDirection,
  ChangeRequestSource,
} from '../../../../enums/changeRequest';
import { ExecutionItem } from '../../../../interfaces/changeRequest';
import HeaderWithNavigation from '../HeaderWithNavigation';
import { EditChangeRequestProps } from '../../../../interfaces/changeRequestProps';
import { useChangeRequestEditForm } from '../../../../hooks/useChangeRequestEditForm';
import { Notify } from '../../../../services/notificationService';
import { IMPLEMENTABLE_STATUSES } from '../../../../services/changeRequestService';
import { handleImplementRow } from '../../../../services/changeRequestService';
import {
  setChangeRequestId,
  setRevokeAPICalls,
} from '../../../../app/Slices/changeRequestStateSlice';
import { DownOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { getChangeRequest } from '../../../../app/ActionTypes/changeRequestActionTypes';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'input needed', label: 'Input needed' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'skipped', label: 'Skipped', disabled: true },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error', disabled: true },
];

const items: MenuProps['items'] = [
  {
    label: 'Save',
    key: 'save',
  },
];

const EditChangeRequestAction: React.FC<
  EditChangeRequestProps<ExecutionItem>
> = ({
  editingRecord,
  onClose,
  onNavigate,
  onSave,
  itemCount,
  isDirty,
  setIsDirty,
  expandToFullScreen,
}) => {
  // Get ID from URL if present
  const { changeRequestRouteId } = useParams<{
    changeRequestRouteId: string;
  }>();
  const [form] = Form.useForm();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { data, loading, source, isImplementing } = useAgentSelector(
    (state) => state.changeRequest
  );
  const changeRequestId = useAgentSelector(
    (state) => state.changeRequestState.changeRequestId
  );
  const agentDispatch = useAgentDispatch();
  const _loading =
    source === ChangeRequestSource.EDIT_ACTIONS_EXECUTION && loading;
  const [updatedRecord, setUpdatedRecord] =
    useState<ExecutionItem>(editingRecord);

  const { handleClose, handleNavigate, onValuesChange } =
    useChangeRequestEditForm({
      onClose,
      onNavigate,
      isDirty,
      setIsDirty,
    });
  const isSaveButtonDisabled = _loading || isImplementing || !isDirty;

  useEffect(() => {
    form.resetFields();
    setIsDirty(false);
    setUpdatedRecord(editingRecord);
  }, [editingRecord]);

  useEffect(() => {
    if (source === ChangeRequestSource.ACTIONS_IMPLEMENT_ONE && !loading) {
      const updatedValues = data?.result.execution.items.find(
        (item) => item.id === editingRecord.id
      );
      if (updatedValues) {
        editingRecord = updatedValues;
        form.setFieldsValue(updatedValues);
        setUpdatedRecord(updatedValues);
        setIsDirty(false);
      }
    }
  }, [loading, source]);

  useEffect(() => {
    const item = data?.result.execution.items.find(
      (item) => item.id === updatedRecord.id
    );
    const updatedValues = { ...updatedRecord, ...item };
    const isModified =
      JSON.stringify(updatedValues) !== JSON.stringify(updatedRecord);
    if (isModified) {
      editingRecord = updatedValues;
      form.setFieldsValue(updatedValues);
      setUpdatedRecord(updatedValues);
      setIsDirty(false);
    }
  }, [data?.result.execution.items]);

  const saveChanges = async (navigate = true) => {
    try {
      const formValues = form.getFieldsValue();
      const hasDetailsChanged = editingRecord.details !== formValues.details;
      const updatedRecord = {
        ...editingRecord,
        ...formValues,
        ...(hasDetailsChanged && {
          parameters: [],
          function: null,
        }),
      };

      const updatedDataResult = await onSave(updatedRecord, navigate);
      setIsDirty(false);
      return updatedDataResult;
    } catch (error) {
      Notify.error('Error saving change request action');
    }
  };

  const implementChanges = async () => {
    try {
      if (!changeRequestId) {
        Notify.error('No change request selected');
        return;
      }

      agentDispatch(setRevokeAPICalls(false));
      let updatedDataResult = data?.result;
      if (isDirty) {
        updatedDataResult = await saveChanges(false);
      }
      const index = updatedDataResult?.execution.items.findIndex(
        (i) => i.id === editingRecord.id
      );
      if (index !== undefined && index > -1) {
        updatedDataResult &&
          handleImplementRow(
            agentDispatch,
            updatedDataResult,
            index,
            changeRequestId,
            updatedDataResult?.execution.items[index],
            ChangeRequestSource.ACTIONS_IMPLEMENT_ONE
          );
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleExpandToFullScreen = (expand: boolean) => {
    expand && expandToFullScreen();
    setIsFullScreen(expand);
  };

  const handleNavigateWithRemount = (direction: ChangeRequestDirection) => {
    handleNavigate(direction);
  };

  const revokeAPICall = () => {
    agentDispatch(setRevokeAPICalls(true));
    if (changeRequestRouteId) {
      agentDispatch(setChangeRequestId(changeRequestRouteId));
      agentDispatch(getChangeRequest(changeRequestRouteId));
    }
  };

  const getDisabledReason = (field: string, record: ExecutionItem): string => {
    if (_loading) return 'Action in progress...';
    if (isImplementing) return 'Implementation is in progress.';
    // TODO Use constants for all the hard-coded column names, status etc
    switch (field) {
      case 'item':
      case 'type':
      case 'details':
        return record.status === 'completed'
          ? 'Cannot edit as this record is marked as completed.'
          : '';

      case 'leftButton':
        // TODO Consolidate with very similar code in ChangeRequestActionUtility.ts
        if (record.type === 'manual task')
          return 'Manual tasks cannot be implemented automatically.';
        if (!IMPLEMENTABLE_STATUSES.includes(record.status)) {
          return `"${record.status.toUpperCase()}" cannot be implemented. Only "PENDING", "INPUT NEEDED" and "ERROR" statuses can be implemented.`;
        }
        return '';

      default:
        return '';
    }
  };

  return (
    <>
      {isFullScreen && <div className="custom-modal-overlay"></div>}
      <div
        id="edit-change-request-action"
        className={`edit-change-request extra-panel ${isFullScreen ? 'full-screen custom-modal' : ''}`}
      >
        <HeaderWithNavigation
          title={editingRecord.item}
          onClose={handleClose}
          onNavigate={handleNavigateWithRemount}
          itemCount={itemCount}
          expandToFullScreen={handleExpandToFullScreen}
        />
        <div className="edit-change-request-body-container">
          <Form
            form={form}
            onValuesChange={(changedFields, allFields) => {
              setUpdatedRecord(onValuesChange(editingRecord, allFields));
            }}
            layout="vertical"
            className="edit-change-request-body"
            initialValues={{ layout: 'vertical', ...editingRecord }}
            data-hs-do-not-collect="true"
          >
            <Tooltip title={getDisabledReason('item', updatedRecord)}>
              <Form.Item
                label="Item"
                name="item"
                rules={[{ required: true, message: 'Please input item!' }]}
              >
                <Input
                  disabled={
                    _loading ||
                    isImplementing ||
                    updatedRecord.status === 'completed'
                  }
                />
              </Form.Item>
            </Tooltip>

            <Tooltip title={getDisabledReason('type', updatedRecord)}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: 'Please input type!' }]}
              >
                <Select
                  disabled={
                    _loading ||
                    isImplementing ||
                    updatedRecord.status === 'completed'
                  }
                >
                  <Select.Option value="automated task">
                    Automated task
                  </Select.Option>
                  <Select.Option value="manual task">Manual task</Select.Option>
                </Select>
              </Form.Item>
            </Tooltip>

            <Tooltip title={getDisabledReason('details', updatedRecord)}>
              <Form.Item
                label="Details"
                name="details"
                rules={[{ required: true, message: 'Please input details!' }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 10 }}
                  disabled={
                    _loading ||
                    isImplementing ||
                    updatedRecord.status === 'completed'
                  }
                />
              </Form.Item>
            </Tooltip>

            {(editingRecord.results || updatedRecord.results) && (
              <Tooltip title="Results cannot be edited.">
                <Form.Item
                  label="Result"
                  name="results"
                  validateStatus={
                    updatedRecord.status === 'completed'
                      ? 'success'
                      : updatedRecord.status === 'error'
                        ? 'error'
                        : 'warning'
                  }
                >
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 10 }}
                    disabled
                  />
                </Form.Item>
              </Tooltip>
            )}

            <Tooltip title={getDisabledReason('status', updatedRecord)}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please input status!' }]}
              >
                <Select disabled={_loading || isImplementing}>
                  {statusOptions.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Tooltip>

            <div className="form-actions">
              {!isImplementing && (
                <Tooltip title={_loading ? 'Action in progress...' : ''}>
                  <Button onClick={handleClose} disabled={_loading}>
                    Close
                  </Button>
                </Tooltip>
              )}
              {isImplementing && (
                <Tooltip title={'Cancel current implementation'}>
                  <Button onClick={() => revokeAPICall()}>Cancel</Button>
                </Tooltip>
              )}
              {updatedRecord.type === 'manual task' ? (
                <Tooltip
                  title={isSaveButtonDisabled ? 'No updates to save' : ''}
                >
                  <Button
                    type="primary"
                    onClick={() => saveChanges()}
                    disabled={isSaveButtonDisabled}
                  >
                    Save
                  </Button>
                </Tooltip>
              ) : (
                <Dropdown.Button
                  type="primary"
                  icon={<DownOutlined />}
                  menu={{
                    items,
                    onClick: () => saveChanges(),
                    disabled: isSaveButtonDisabled,
                  }}
                  buttonsRender={([leftButton, rightButton]) => {
                    const isLeftButtonDisabled =
                      _loading ||
                      isImplementing ||
                      updatedRecord.type === 'manual task' ||
                      !IMPLEMENTABLE_STATUSES.includes(updatedRecord.status);

                    return [
                      <Tooltip
                        key="leftButton"
                        title={getDisabledReason('leftButton', updatedRecord)}
                      >
                        {React.cloneElement(leftButton as React.ReactElement, {
                          disabled: isLeftButtonDisabled,
                        })}
                      </Tooltip>,
                      <Tooltip
                        key="rightButton"
                        title={isSaveButtonDisabled ? 'No updates to save' : ''}
                      >
                        {React.cloneElement(rightButton as React.ReactElement, {
                          disabled: isSaveButtonDisabled,
                        })}
                      </Tooltip>,
                    ];
                  }}
                  onClick={() => implementChanges()}
                >
                  {updatedRecord.status === 'pending'
                    ? isDirty
                      ? 'Save and Implement'
                      : 'Implement'
                    : updatedRecord.status === 'error' ||
                        updatedRecord.status === 'input needed'
                      ? isDirty
                        ? 'Save and Retry Implementation'
                        : 'Retry Implementation'
                      : 'Implement'}
                </Dropdown.Button>
              )}
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default EditChangeRequestAction;
